import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';

type EventCallback = (data: any) => void;

// Define the context type
interface SocketContextType {
  messages: string[];
  sendMessage: (message: string) => void;
  connectionStatus: string;
  setUrl: (url: string) => void; // Add a function to update the URL
  listenToEvent: (event: string, callback: EventCallback) => void;
  unlistenToEvent: (event: string, callback: EventCallback) => void;
}

// Define the provider props type
interface SocketProviderProps {
  children: ReactNode;
  initialUrl: string;
  /** false = never connect (public pages with no admin session). */
  enabled?: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, initialUrl, enabled = true }) => {
  const [messages, setMessages] = useState<string[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  const [url, setUrl] = useState<string>(initialUrl);

  // Live socket + the handlers callers registered. listenToEvent used to no-op
  // when `socket` state was still null (child useEffects run before this
  // provider's connect effect) and never rebound after setUrl replaced the
  // instance. The registry is the source of truth; every new socket gets the
  // same bindings.
  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef<Map<string, Set<EventCallback>>>(new Map());

  useEffect(() => {
    // The socket created by this effect run. Cleanup disconnects THIS instance
    // rather than the `socket` state (which is stale inside the effect closure),
    // so changing `url` — e.g. switching the viewed club — tears down the old
    // namespace before connecting to the new one.
    let activeSocket: Socket | null = null;
    if (!enabled) return undefined;

    const attachRegisteredListeners = (target: Socket) => {
      listenersRef.current.forEach((callbacks, event) => {
        callbacks.forEach((cb) => {
          target.on(event, cb);
        });
      });
    };

    const connectSocket = () => {
      setConnectionStatus('Connecting...');
      try {
        const newSocket = io(url, {
          transports: ['websocket'], // Ensure it uses WebSocket if possible
        });
        activeSocket = newSocket;
        socketRef.current = newSocket;
        attachRegisteredListeners(newSocket);

        newSocket.on('connect', () => {
          setConnectionStatus('Connected');
        });

        newSocket.on('message', (message: string) => {
          setMessages((prevMessages) => [...prevMessages, message]);
        });

        newSocket.on('disconnect', (reason) => {
          if (reason === 'io server disconnect') {
            // Server disconnected, attempt reconnect
            setConnectionStatus('Server disconnected. Reconnecting...');
            newSocket.connect();
          } else if (reason === 'transport close') {
            // Connection lost, likely network issue or server restart
            setConnectionStatus('Connection lost. Reconnecting...');
          } else {
            setConnectionStatus('Disconnected');
          }
        });

        newSocket.on('connect_error', (error) => {
          const errorMessage = error?.message || 'Unknown error';
          if (errorMessage.includes('timeout')) {
            setConnectionStatus('Connection timeout. Retrying...');
          } else if (errorMessage.includes('xhr poll error')) {
            setConnectionStatus('Network error. Retrying...');
          } else {
            setConnectionStatus('Connection error. Retrying...');
          }
          console.error('Socket connection error:', errorMessage);
        });

        newSocket.on('reconnect', (attemptNumber) => {
          console.log('Socket reconnected after', attemptNumber, 'attempts');
          setConnectionStatus('Connected');
        });

        newSocket.on('reconnect_attempt', (attemptNumber) => {
          setConnectionStatus(`Reconnecting (attempt ${attemptNumber})...`);
        });

        newSocket.on('reconnect_failed', () => {
          setConnectionStatus('Reconnection failed. Please refresh.');
        });

        setSocket(newSocket);
      } catch {
        setConnectionStatus('Error (Invalid URL or Server Down)');
        setTimeout(connectSocket, 5000);
      }
    };

    connectSocket();

    return () => {
      if (activeSocket) {
        activeSocket.disconnect();
      }
      if (socketRef.current === activeSocket) {
        socketRef.current = null;
      }
    };
  }, [url, enabled]); // Reconnect if the URL changes

  const sendMessage = (message: string) => {
    if (socket && socket.connected) {
      socket.emit('message', message);
    }
  };

  const listenToEvent = useCallback((event: string, callback: EventCallback) => {
    let bucket = listenersRef.current.get(event);
    if (!bucket) {
      bucket = new Set();
      listenersRef.current.set(event, bucket);
    }
    if (bucket.has(callback)) {
      return;
    }
    bucket.add(callback);
    socketRef.current?.on(event, callback);
  }, []);

  const unlistenToEvent = useCallback((event: string, callback: EventCallback) => {
    const bucket = listenersRef.current.get(event);
    if (bucket) {
      bucket.delete(callback);
      if (bucket.size === 0) {
        listenersRef.current.delete(event);
      }
    }
    socketRef.current?.off(event, callback);
  }, []);

  return (
    <SocketContext.Provider value={{ messages, sendMessage, connectionStatus, setUrl, listenToEvent, unlistenToEvent }}>
      {children}
    </SocketContext.Provider>
  );
};
